// src/screens/ProgressIndicator.jsx
import React from 'react';

const PEACH = '#EBA477';

const DEFAULT_STEPS = [
  { key: 'account',    title: 'Account Setup',        description: 'Profile & basics' },
  { key: 'business',   title: 'Add a Business',       description: 'Company details' },
  { key: 'locations',  title: 'Set Locations',        description: 'Addresses & hours' },
  { key: 'agreements', title: 'Agreements', description: 'Terms & approvals' },
  { key: 'review',     title: 'Review & Verify',      description: 'Confirm & submit' },
];

/**
 * Props:
 * - currentStep: number (1..totalSteps of your internal flow)
 * - totalSteps:  number (internal flow count; if omitted, defaults to steps.length)
 * - steps:       optional custom array to override DEFAULT_STEPS
 * - onStepClick: optional (idx) => void
 */
const ProgressIndicator = ({
  currentStep = 1,
  totalSteps,
  steps = DEFAULT_STEPS,
  onStepClick,
}) => {
  const internalTotal = typeof totalSteps === 'number' && totalSteps > 0
    ? totalSteps
    : steps.length;

  // Map your internal step (1..internalTotal) onto the 5 visible milestones
  const visibleIndex = Math.min(
    steps.length,
    Math.max(1, Math.ceil((currentStep / internalTotal) * steps.length))
  );

  const progressPct = steps.length > 1
    ? ((visibleIndex - 1) / (steps.length - 1)) * 100
    : 100;

  return (
    <div style={sx.wrap}>
      {/* Progress bar */}
      <div style={sx.barTrack}>
        <div style={{ ...sx.barFill, width: `${progressPct}%` }} />
      </div>

      {/* Steps */}
      <div style={{ ...sx.stepsRow, gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
        {steps.map((step, i) => {
          const idx = i + 1;
          const state = idx < visibleIndex ? 'completed'
                      : idx === visibleIndex ? 'active'
                      : 'pending';

          return (
            <div
              key={step.key || idx}
              style={sx.stepItem}
              onClick={onStepClick ? () => onStepClick(idx) : undefined}
              role={onStepClick ? 'button' : undefined}
            >
              <div
                style={{
                  ...sx.circle,
                  ...(state === 'completed' && sx.circleCompleted),
                  ...(state === 'active' && sx.circleActive),
                }}
              >
                {state === 'completed' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  idx
                )}
              </div>

              <div style={sx.textBlock}>
                <div style={sx.title}>{step.title}</div>
                <div style={sx.desc}>{step.description}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const sx = {
  wrap: {
    width: '100%',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
  },
  barTrack: {
    width: '100%',
    height: 6,
    background: 'rgba(255,255,255,0.18)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    background: PEACH,
    borderRadius: 8,
    transition: 'width 220ms ease',
  },
  stepsRow: {
    display: 'grid',
    gap: 16,
    marginTop: 12,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  circle: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.18)',
    display: 'grid',
    placeItems: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: 'white',
  },
  circleCompleted: {
    color: '#1d1d1d',
    background: PEACH,
    border: `1px solid ${PEACH}`,
  },
  circleActive: {
    border: `2px solid ${PEACH}`,
    boxShadow: '0 0 0 3px rgba(235,164,119,0.25)',
  },
  textBlock: {
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.15,
  },
  desc: {
    fontSize: 12,
    opacity: 0.85,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

export default ProgressIndicator;
